import { useMemo } from "react";

import useWindowDimensions from "./useWindowDimensions";

type Breakpoints = {
  heightSmallPhone: number;
  smallPhone: number;
  phone: number;
  tablet: number;
  tabletLandscape: number;
  laptop: number;
  desktop: number;
};

const useBreakpoints = () => {
  const { width, height } = useWindowDimensions();

  const readCssBreakpoint = (name: string) =>
    parseInt(
      getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .replace("px", ""),
    );

  const cssBreakpoints: Breakpoints = useMemo(() => {
    return {
      desktop: readCssBreakpoint("--breakpoints-desktop"),
      heightSmallPhone: readCssBreakpoint("--breakpoints-height-small-phone"),
      laptop: readCssBreakpoint("--breakpoints-laptop"),
      phone: readCssBreakpoint("--breakpoints-phone"),
      smallPhone: readCssBreakpoint("--breakpoints-small-phone"),
      tablet: readCssBreakpoint("--breakpoints-tablet"),
      tabletLandscape: readCssBreakpoint("--breakpoints-tablet-landscape"),
    };
  }, []);

  const isMobileVersion = useMemo(
    () => width <= cssBreakpoints.tablet,
    [cssBreakpoints, width],
  );

  const isTabletVersion = useMemo(() => {
    return width < cssBreakpoints.tablet;
  }, [width, cssBreakpoints]);

  const isSmallPhone = useMemo(
    () => height <= cssBreakpoints.heightSmallPhone,
    [cssBreakpoints, width],
  );

  const isTabletOrHigher = useMemo(() => {
    return width >= cssBreakpoints.tablet;
  }, [width, cssBreakpoints]);

  const isLaptopOrHigher = useMemo(() => {
    return width >= cssBreakpoints.laptop;
  }, [width, cssBreakpoints]);

  const isDesktopOrHigher = useMemo(() => {
    return width >= cssBreakpoints.desktop;
  }, [width, cssBreakpoints]);

  return {
    cssBreakpoints,
    isDesktopOrHigher,
    isLaptopOrHigher,
    isMobileVersion,
    isSmallPhone,
    isTabletOrHigher,
    isTabletVersion,
  };
};

export default useBreakpoints;
